/**
 * @Description 新建匹配点位
 */
import React, { FC, useEffect, useState } from 'react';
import { Form, Modal } from 'antd';
import { Typography } from 'antd';
import { useMethods } from '@lhb/hook';
import SearchForm from '@/common/components/Form/SearchForm';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import V2FormProvinceList from '@/common/components/Form/V2FormProvinceList';
import V2FormRangePicker from '@/common/components/Form/V2FormRangePicker/V2FormRangePicker';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import V2Table from '@/common/components/Data/V2Table';
import { getChancepointSelection, getDynamicDetail } from '@/common/api/expandStore/chancepoint';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import dayjs from 'dayjs';
import { Cascader } from 'antd';
import { isArray } from '@lhb/func';
import { tenantCheck } from '@/common/api/common';
import { relateToChancePoint } from '@/common/api/passengerflowcount';
const { Text } = Typography;

interface Props {
  id:number// 点位id
  data:{visible:boolean, detail:any}, // 打开弹窗
  setData: Function // 控制是否打开弹窗
  refresh: Function // 刷新详情
  chancepointAlias?: string;
}


const RelativePointMatching : FC<Props> = ({
  id,
  data,
  setData,
  refresh,
  chancepointAlias,
}) => {
  const [form] = Form.useForm(); // 表单参数
  const [filters, setFilters] = useState<any>({}); // 筛选参数
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]); // 选择的关联的点位ids
  const [statuses, setStatuses] = useState<any>([]); // 机会点状态选项
  const [isYiHeTang, setIsYiHeTang] = useState<boolean>(false);

  useEffect(() => {
    tenantCheck().then(({ isYiHeTang }) => {
      setIsYiHeTang(!!isYiHeTang);
    });
    getChancepointSelection({}).then(({ relateStatus }: any) => {
      isArray(relateStatus) && setStatuses(relateStatus);
    });
  }, []);


  const methods = useMethods({
    /**
     * @description 点击-取消
     */
    onCancel() {
      // console.log('onCancel');
      setSelectedRowKeys([]); // 清空选择
      setData({
        ...data,
        visible: false
      }); // 关闭弹窗
      setFilters({});
      form.resetFields(); // 重置表单
    },

    /**
     * @description 点击确认-新增匹配点位
     */
    onOk() {
      if (!selectedRowKeys.length) {
        V2Message.warning('请先选择匹配的点位');
        return;
      }
      relateToChancePoint({ id, chancePointIds: selectedRowKeys }).then(() => {
        V2Message.success('添加匹配点位成功');
        methods.onCancel();
        refresh();
      }).catch(() => {
        V2Message.error('添加匹配点位失败');
      });
    },

    /**
     * @description 搜索
     * @param params 搜索关键词
     */
    onSearch(params) {
      // console.log('params 原始', params);
      const { houseDelivery, addresses } = params;
      let houseDeliveryStart;
      let houseDeliveryEnd;
      let tmpAddresses: any;
      // 省市区参数处理
      if (isArray(addresses) && addresses.length) {
        tmpAddresses = addresses.map(itm => ({
          provinceId: itm.length ? itm[0] : undefined,
          cityId: itm.length > 1 ? itm[1] : undefined,
          districtId: itm.length > 2 ? itm[2] : undefined,
        }));
      }
      // 日期参数处理
      if (isArray(houseDelivery) && houseDelivery.length === 2) {
        houseDeliveryStart = dayjs(houseDelivery[0]).format('YYYY-MM-DD');
        houseDeliveryEnd = dayjs(houseDelivery[1]).format('YYYY-MM-DD');
      }
      params = {
        ...params,
        houseDelivery: undefined,
        houseDeliveryStart,
        houseDeliveryEnd,
        addresses: tmpAddresses,
      };
      // console.log('params 格式后', params);
      setFilters(params);
    },

    /**
     * @description 加载表格数据
     * @param params 搜索参数
     * @returns 表格数据
     */
    async loadData(params) {
      const _data = await getDynamicDetail({
        ...params,
        latitude: data.detail?.lat,
        longitude: data.detail?.lng,
        distance: 3000,
        dynamicTemplateType: 1,
      });
      return {
        dataSource: _data.objectList || [],
        count: _data.totalNum || 0,
      };
    }
  });

  const defaultColumns = [{
    key: 'name',
    title: `${chancepointAlias}名称`,
    width: 174,
    dragChecked: true,
    dragDisabled: true,
    render: (val) => val || '-'
  }, {
    key: 'address',
    width: 'auto',
    title: '详细地址',
    dragChecked: true,
    dragDisabled: true,
    render: (val) => val || '-'
  }
  ];

  /** V2Table中多选配置项*/
  const rowSelection = {
    type: 'checkbox',
    selectedRowKeys,
    preserveSelectedRowKeys: true, // 保留上一次选择参数，不设置会丢失之前的多选参数
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  return (
    <>
      <Modal
        title={`关联${chancepointAlias || '机会点'}`}
        open={data.visible}
        onOk={methods.onOk}
        width={740}
        onCancel={methods.onCancel}
        destroyOnClose
      >
        <SearchForm
          onOkText='搜索'
          form={form}
          labelLength={5}
          showResetBtn
          onSearch={methods.onSearch}
        >
          <V2FormInput
            // label='机会点名称'
            label={
              <Text
                style={{ width: 116 }}
                ellipsis={{ tooltip: `${chancepointAlias || '机会点'}名称` }}>
                {chancepointAlias}名称
              </Text>
            }
            name='name'
            placeholder='请输入店铺名称或地址搜索'
          />
          <V2FormProvinceList
            label='省市区'
            name='addresses'
            type={1}
            multiple
            config={{ showCheckedStrategy: Cascader.SHOW_PARENT }}/>
          {isYiHeTang ? <V2FormRangePicker label='可交房日期' name='houseDelivery'/> : <></>}
          <V2FormSelect
            // label='机会点状态'
            label={
              <Text
                style={{ width: 116 }}
                ellipsis={{ tooltip: `${chancepointAlias || '机会点'}状态` }}>
                {chancepointAlias}状态
              </Text>
            }
            name='statuses'
            options={statuses}
            config={{
              mode: 'multiple',
              maxTagCount: 'responsive',
              showSearch: true,
              filterOption: false,
              fieldNames: {
                label: 'name',
                value: 'id'
              },
            }}/>

        </SearchForm>

        <V2Table
          rowKey='id'
          defaultColumns={defaultColumns}
          onFetch={methods.loadData}
          // pagination={false}
          hideColumnPlaceholder={true}
          filters={filters}
          rowSelection={rowSelection}
          scroll={{ x: 'max-content', y: 368 }}
        />

      </Modal>
    </>
  );
};
export default RelativePointMatching;

